import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Image,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import deadlineIcon from 'assets/img/selfIpo/deadline.svg';
import dealFeeIcon from 'assets/img/selfIpo/deal-fee.svg';
import lockUpDaysIcon from 'assets/img/selfIpo/lock-up-days.svg';
import minimumAmountIcon from 'assets/img/selfIpo/minimum-amount.svg';
import priceRangeIcon from 'assets/img/selfIpo/price-range.svg';
import statusIcon from 'assets/img/selfIpo/status.svg';
import successFeeIcon from 'assets/img/selfIpo/success-fee.svg';
import useToastError from 'hooks/shared/useToastError';
import {
  formatSelfIpoDateTime,
  formatSelfIpoMoneyAmount,
  getSelfIpoCurrentApplicationInfoRows,
  getSelfIpoDetailAction,
  getSelfIpoEligibilityText,
} from 'presentation/components/modules/selfIpo/selfIpoUtils';
import AppCard from 'presentation/components/shared/card/AppCard';
import CardInfoRow from 'presentation/components/shared/card/CardInfoRow';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import FullScreenLoader from 'presentation/components/shared/FullScreenLoader';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import HtmlContentBox from 'presentation/components/shared/HtmlContentBox/HtmlContentBox';
import SelfIpoParamRow from 'presentation/components/modules/selfIpo/SelfIpoParamRow';
import TagsRow, { TagSize } from 'presentation/components/shared/TagsRow';
import PageLayout from 'presentation/layout/shared/page/PageLayout';
import ApplySelfIpoModal from 'presentation/containers/modules/selfIpo/ApplySelfIpoModal';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import {
  useApplySelfIpoOfferingMutation,
  useFetchSelfIpoOfferingDetailQuery,
  useFetchSelfIpoPortfolioQuery,
  useTopUpSelfIpoMutation,
} from 'rtkQuery/sharespro/queries/selfIpo';

const SelfIpoDetailPage: FC = () => {
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const toastErrorHandler = useToastError();
  const location = useLocation();
  const { id } = useParams();
  const offeringId = +(id as string);
  const openedFromApplications =
    (location.state as { openedFromApplications?: boolean } | null)
      ?.openedFromApplications === true;
  const {
    isOpen: isApplyModalOpen,
    onClose: onApplyModalClose,
    onOpen: onApplyModalOpen,
  } = useDisclosure();
  const {
    data: detail,
    error,
    isFetching,
    refetch,
  } = useFetchSelfIpoOfferingDetailQuery(offeringId);
  const { data: portfolio, refetch: refetchPortfolio } =
    useFetchSelfIpoPortfolioQuery();
  const [applySelfIpoOffering, { isLoading: isApplyOfferingLoading }] =
    useApplySelfIpoOfferingMutation();
  const [topUpSelfIpo, { isLoading: isTopUpLoading }] =
    useTopUpSelfIpoMutation();

  let content;
  if (detail) {
    const offering = detail.item;
    const title = `${offering.tickerCode} - ${offering.companyName}`;
    const detailAction = getSelfIpoDetailAction(detail.eligibility.status);
    const isActionLoading = isApplyOfferingLoading || isTopUpLoading;
    const currentApplicationInfoRows = detail.myApplication
      ? getSelfIpoCurrentApplicationInfoRows(t, detail.myApplication)
      : [];

    const handleSuccess = (description: string): void => {
      toast({
        description,
        status: 'success',
        position: 'top-right',
        isClosable: true,
      });
      refetch();
      refetchPortfolio();
    };

    const handleDetailActionClick = (): void => {
      if (detailAction === 'apply') {
        onApplyModalOpen();
      } else if (detailAction === 'apply_offering') {
        applySelfIpoOffering({ offering_id: offering.id })
          .unwrap()
          .then(() => handleSuccess(t('selfIpoContactManagerSuccess')))
          .catch((err) => toastErrorHandler(err));
      } else if (detailAction === 'top_up') {
        topUpSelfIpo({ offering_id: offering.id })
          .unwrap()
          .then(() => handleSuccess(t('selfIpoTopUpSuccess')))
          .catch((err) => toastErrorHandler(err));
      }
    };

    const detailActionText =
      detailAction === 'apply'
        ? t('selfIpoApplyAction')
        : detailAction === 'apply_offering'
        ? t('selfIpoContactManagerAction')
        : t('selfIpoTopUpAction');

    content = (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <GridHeading
          trailingLink={
            openedFromApplications
              ? undefined
              : {
                  path: AppRoute.selfIpoApplications,
                  text: t('myOrdersLabel'),
                  fontWeight: 700,
                }
          }
        >
          {offering.companyName}
        </GridHeading>

        <AppCard p={{ base: '20px', md: '40px' }} mb="20px">
          <Flex
            gap={{ base: '24px', lg: '40px' }}
            flexDir={{ base: 'column', lg: 'row' }}
          >
            <Flex flex="1" flexDir="column" justifyContent="center">
              <Badge
                alignSelf="flex-start"
                colorScheme={offering.isAccepting ? 'green' : 'gray'}
                borderRadius="10px"
                px="12px"
                py="6px"
                textTransform="uppercase"
              >
                {offering.tickerCode}
              </Badge>
              <Text
                mt="28px"
                fontWeight={800}
                fontSize={{ base: '30px', md: '40px' }}
                lineHeight="1.12"
              >
                {offering.companyName}
              </Text>
              <Text mt="10px" color={Color.Shade600} fontSize="20px">
                {offering.sector}
              </Text>
              <TagsRow
                mt="26px"
                tags={offering.tags}
                size={TagSize.small}
                tagColor={Color.Primary500}
              />
              <Box mt="28px">
                <SelfIpoParamRow
                  iconSrc={priceRangeIcon}
                  label={t('selfIpoPriceRange')}
                >
                  <Text>
                    ${formatSelfIpoMoneyAmount(offering.priceLow)} - $
                    {formatSelfIpoMoneyAmount(offering.priceHigh)}
                  </Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={minimumAmountIcon}
                  label={t('selfIpoMinAmount')}
                >
                  <Text>${formatSelfIpoMoneyAmount(offering.minAmount)}</Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={dealFeeIcon}
                  label={t('selfIpoDealFee')}
                >
                  <Text>{offering.dealFee}%</Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={successFeeIcon}
                  label={t('selfIpoSuccessFee')}
                >
                  <Text>{offering.successFee}%</Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={lockUpDaysIcon}
                  label={t('selfIpoLockUpDays')}
                >
                  <Text>{offering.lockUp}</Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={deadlineIcon}
                  label={t('selfIpoAcceptanceDeadline')}
                >
                  <Text>
                    {formatSelfIpoDateTime(
                      offering.acceptanceDeadline,
                      i18n.language
                    )}
                  </Text>
                </SelfIpoParamRow>
                <SelfIpoParamRow
                  iconSrc={statusIcon}
                  label={t('status')}
                  showBorder={false}
                >
                  <Text
                    color={
                      detail.eligibility.status === 'ready'
                        ? Color.Primary500
                        : Color.Error
                    }
                  >
                    {getSelfIpoEligibilityText(t, detail.eligibility)}
                  </Text>
                </SelfIpoParamRow>
              </Box>
            </Flex>
            <Box
              flex="1"
              minH={{ base: '280px', md: '420px' }}
              bgColor={Color.Shade100}
              borderRadius="8px"
              overflow="hidden"
            >
              {offering.coverImageUrl && (
                <Image
                  src={offering.coverImageUrl}
                  alt={offering.companyName}
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              )}
            </Box>
          </Flex>
        </AppCard>

        {portfolio && portfolio.balance !== 0 && (
          <AppCard p="20px" mb="20px">
            <Text fontSize="20px" fontWeight={700} mb="10px">
              {t('selfIpoPortfolio')}
            </Text>
            <CardInfoRow label={t('selfIpoCashBalance')}>
              <Text>${formatSelfIpoMoneyAmount(portfolio.cashBalance)}</Text>
            </CardInfoRow>
            <CardInfoRow label={t('selfIpoHoldBalance')}>
              <Text>${formatSelfIpoMoneyAmount(portfolio.holdBalance)}</Text>
            </CardInfoRow>
            <CardInfoRow label={t('selfIpoAssetBalance')}>
              <Text>${formatSelfIpoMoneyAmount(portfolio.assetBalance)}</Text>
            </CardInfoRow>
            <CardInfoRow label={t('selfIpoBalance')} showBorder={false}>
              <Text>${formatSelfIpoMoneyAmount(portfolio.balance)}</Text>
            </CardInfoRow>
          </AppCard>
        )}

        {offering.description && (
          <AppCard p="20px" mb="20px">
            <Text fontSize="20px" fontWeight={700} mb="10px">
              {t('selfIpoDescription')}
            </Text>
            <HtmlContentBox content={offering.description} />
          </AppCard>
        )}

        {detail.myApplication && (
          <AppCard p="20px" mb="20px">
            <Text fontSize="20px" fontWeight={700} mb="10px">
              {t('selfIpoCurrentApplication')}
            </Text>
            {currentApplicationInfoRows.map((row, index) => (
              <CardInfoRow
                key={row.label}
                label={row.label}
                showBorder={index !== currentApplicationInfoRows.length - 1}
              >
                <Text>{row.value}</Text>
              </CardInfoRow>
            ))}
          </AppCard>
        )}

        {detailAction && (
          <Box
            position="sticky"
            bottom="0"
            zIndex={10}
            pt="4px"
            pb="18px"
            bg="transparent"
            _before={{
              content: '""',
              position: 'absolute',
              left: '-18px',
              right: '-18px',
              bottom: '0',
              h: '96px',
              bg: 'rgba(245, 245, 245, 0.9)',
              filter: 'blur(18px)',
              borderRadius: '24px',
              pointerEvents: 'none',
            }}
          >
            <Button
              position="relative"
              zIndex={1}
              w="100%"
              isLoading={isActionLoading}
              onClick={handleDetailActionClick}
            >
              {detailActionText}
            </Button>
          </Box>
        )}
        <ApplySelfIpoModal
          offering={offering}
          portfolio={portfolio}
          isOpen={isApplyModalOpen}
          onClose={onApplyModalClose}
          onSuccess={() => {
            refetch();
            refetchPortfolio();
          }}
        />
        <FullScreenLoader isLoading={isActionLoading} />
      </>
    );
  } else if (isFetching) {
    content = (
      <AppCard p="20px">
        <Center>
          <Spinner />
        </Center>
      </AppCard>
    );
  } else if (error) {
    content = (
      <AppCard p="20px">
        <Center>
          <ErrorContainer
            error={error}
            onRetry={() => {
              refetch();
            }}
          />
        </Center>
      </AppCard>
    );
  } else {
    content = (
      <AppCard p="20px">
        <Center>
          <Spinner />
        </Center>
      </AppCard>
    );
  }

  return (
    <PageLayout>
      {!detail && (
        <Helmet>
          <title>{t('selfIpoTitle')}</title>
        </Helmet>
      )}
      <Container>{content}</Container>
    </PageLayout>
  );
};

export default SelfIpoDetailPage;
